import type { Dispatch, SetStateAction } from 'react';

import type { CustomQuizQuestion, QuizDetail } from '@cfa/pathway-fe-shared/xp';

export type QuizLocale = 'en' | 'es';

export type V2QuizEditorQuestion = CustomQuizQuestion & {
  /** Client-only id for React keys and saving indicator. */
  clientId: string;
};

export interface V2QuizEditorState {
  quizId: string;
  ownerId: string;
  primaryLanguage: QuizLocale;
  name: { en?: string; es?: string };
  passingGrade: number;
  questions: V2QuizEditorQuestion[];
  /** Index of the question currently being saved, if any. */
  savingQuestionIndex: number | null;
  isSaving: boolean;
}

export type SetV2QuizEditorState = Dispatch<SetStateAction<V2QuizEditorState>>;

export const createClientQuestionId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export const createEmptyMultipleChoiceQuestion = (): V2QuizEditorQuestion => ({
  clientId: createClientQuestionId(),
  questionType: 'mc',
  question: { en: '', es: '' },
  answers: [
    { answer: { en: '', es: '' }, correct: false },
    { answer: { en: '', es: '' }, correct: false },
    { answer: { en: '', es: '' }, correct: false },
  ],
});

export const createEmptyTrueFalseQuestion = (): V2QuizEditorQuestion => ({
  clientId: createClientQuestionId(),
  questionType: 'tf',
  question: { en: '', es: '' },
  answers: [
    {
      answer: { en: 'True', es: 'Cierto' },
      correct: false,
    },
    {
      answer: { en: 'False', es: 'Falso' },
      correct: false,
    },
  ],
});

export const createDefaultV2QuizEditorState = (): V2QuizEditorState => ({
  quizId: '',
  ownerId: '',
  primaryLanguage: 'en',
  name: { en: '', es: '' },
  passingGrade: 0,
  questions: [createEmptyMultipleChoiceQuestion()],
  savingQuestionIndex: null,
  isSaving: false,
});

/** Map a GET quiz detail into V2 editor state (adds client-only question ids). */
export const quizDetailToV2EditorState = (
  detail: QuizDetail,
): V2QuizEditorState => ({
  quizId: detail.quizId,
  ownerId: detail.ownerId,
  primaryLanguage: detail.primaryLanguage === 'es' ? 'es' : 'en',
  name: {
    en: detail.name.en,
    es: detail.name.es,
  },
  passingGrade: detail.passingGrade,
  questions: detail.questions.map(question => ({
    ...question,
    clientId: createClientQuestionId(),
  })),
  savingQuestionIndex: null,
  isSaving: false,
});

export const oppositeLanguage = (language: QuizLocale): QuizLocale =>
  language === 'en' ? 'es' : 'en';

type LocalizedTextPatch = Partial<Record<QuizLocale, string>>;

const mapQuestion = (
  state: V2QuizEditorState,
  questionIndex: number,
  map: (question: V2QuizEditorQuestion) => V2QuizEditorQuestion,
): V2QuizEditorState => ({
  ...state,
  questions: state.questions.map((question, index) =>
    index === questionIndex ? map(question) : question,
  ),
});

/** Merge locale strings into a question prompt. */
export const patchQuestionPrompt = (
  state: V2QuizEditorState,
  questionIndex: number,
  patch: LocalizedTextPatch,
): V2QuizEditorState =>
  mapQuestion(state, questionIndex, question => ({
    ...question,
    question: { ...question.question, ...patch },
  }));

/** Merge locale strings into one answer's text. */
export const patchAnswerText = (
  state: V2QuizEditorState,
  questionIndex: number,
  answerIndex: number,
  patch: LocalizedTextPatch,
): V2QuizEditorState =>
  mapQuestion(state, questionIndex, question => ({
    ...question,
    answers: question.answers.map((answer, index) =>
      index === answerIndex
        ? { ...answer, answer: { ...answer.answer, ...patch } }
        : answer,
    ),
  }));

/** Mark exactly one answer correct on a question. */
export const setCorrectAnswer = (
  state: V2QuizEditorState,
  questionIndex: number,
  answerIndex: number,
): V2QuizEditorState =>
  mapQuestion(state, questionIndex, question => ({
    ...question,
    answers: question.answers.map((answer, index) => ({
      ...answer,
      correct: index === answerIndex,
    })),
  }));

export const addEmptyAnswer = (
  state: V2QuizEditorState,
  questionIndex: number,
): V2QuizEditorState =>
  mapQuestion(state, questionIndex, question => ({
    ...question,
    answers: [
      ...question.answers,
      { answer: { en: '', es: '' }, correct: false },
    ],
  }));

export const removeAnswer = (
  state: V2QuizEditorState,
  questionIndex: number,
  answerIndex: number,
): V2QuizEditorState =>
  mapQuestion(state, questionIndex, question => ({
    ...question,
    answers: question.answers.filter((_, index) => index !== answerIndex),
  }));

/** Swap question type/shape but keep clientId + current prompt text. */
export const replaceQuestionKeepingPrompt = (
  state: V2QuizEditorState,
  questionIndex: number,
  replacement: V2QuizEditorQuestion,
): V2QuizEditorState =>
  mapQuestion(state, questionIndex, question => ({
    ...replacement,
    clientId: question.clientId,
    question: question.question,
  }));
